/**
 * Participants section for create/edit field event forms.
 */
import * as React from 'react';
import {
  DndContext,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Users, Pencil } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { UsersListColumn } from '@/components/common/UsersListColumn';
import { UnifiedDropZone } from '@/components/common/UnifiedDropZone';
import { EventFacilitatorPickerModal } from './EventFacilitatorPickerModal';
import { EventStudentPickerModal } from './EventStudentPickerModal';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import {
  UnregisteredStudentForm,
  type UnregisteredStudentData,
  type UnregisteredStudentFormHandle,
} from './UnregisteredStudentForm';
import { prependUserToUsersSearchCaches } from '@/utils/created-user-profile-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { CustomDragOverlay } from '@/components/common/CustomDragOverlay';
import { EVENT_PARTICIPANTS_COLUMN_PAGE_SIZE } from '@/components/events/event-configs';
import {
  formatEventParticipantName,
  isOrganizerRoleGroup,
  roleGroupToOrganizerRole,
} from '@/components/events/event-form-utils';
import type { UserWithProfile } from '@/types/user.types';
/** Props for {@link EventParticipantsSection}. */
export interface EventParticipantsSectionProps {
  selectedOrganizers: UserWithProfile[];
  selectedStudents: UserWithProfile[];
  unregisteredParticipants: UnregisteredStudentData[];
  selectedOrganizerIds: string[];
  selectedStudentIds: string[];
  onAddOrganizer: (user: UserWithProfile) => void;
  onAddStudent: (user: UserWithProfile) => void;
  onRemoveOrganizer: (userId: string) => void;
  onRemoveStudent: (userId: string) => void;
  onAddUnregistered: (data: UnregisteredStudentData) => void;
  onRemoveUnregistered: (index: number) => void;
  onEditUnregistered: (index: number, data: UnregisteredStudentData) => void;
  establishmentId?: string;
  establishmentName?: string;
  onRequestCreateTutor?: () => void;
}

/** Participants section for create/edit field event forms. */
export const EventParticipantsSection: React.FC<EventParticipantsSectionProps> = ({
  selectedOrganizers,
  selectedStudents,
  unregisteredParticipants,
  selectedOrganizerIds,
  selectedStudentIds,
  onAddOrganizer,
  onAddStudent,
  onRemoveOrganizer,
  onRemoveStudent,
  onAddUnregistered,
  onRemoveUnregistered,
  onEditUnregistered,
  establishmentId,
  establishmentName,
  onRequestCreateTutor,
}) => {
  const queryClient = useQueryClient();
  const [isFacilitatorCreationOpen, setIsFacilitatorCreationOpen] = React.useState(false);
  const [isStudentCreationOpen, setIsStudentCreationOpen] = React.useState(false);
  const [activeDragUser, setActiveDragUser] = React.useState<UserWithProfile | null>(null);
  const [editingUnregisteredIdx, setEditingUnregisteredIdx] = React.useState<number | null>(null);
  const editFormRef = React.useRef<UnregisteredStudentFormHandle>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  /** Skip duplicates so the drop zone never lists the same organizer twice. */
  const handleAddOrganizer = React.useCallback(
    (user: UserWithProfile) => {
      if (selectedOrganizerIds.includes(user.id)) {
        const name = formatEventParticipantName(user.firstName, user.lastName);
        toast.info(getErrorMessage({ code: 'eventParticipantAlreadyAdded', name }));
        return;
      }
      onAddOrganizer(user);
    },
    [selectedOrganizerIds, onAddOrganizer],
  );

  /** Skip duplicates so the drop zone never lists the same student twice. */
  const handleAddStudent = React.useCallback(
    (user: UserWithProfile) => {
      if (selectedStudentIds.includes(user.id)) {
        const name = formatEventParticipantName(user.firstName, user.lastName);
        toast.info(getErrorMessage({ code: 'eventParticipantAlreadyAdded', name }));
        return;
      }
      onAddStudent(user);
    },
    [selectedStudentIds, onAddStudent],
  );

  /** Keep the dragged user for {@link CustomDragOverlay}. */
  const handleDragStart = (event: DragStartEvent) => {
    const userData = event.active.data.current?.user as UserWithProfile | undefined;
    if (userData) setActiveDragUser(userData);
  };

  /** Drop onto the zone: organizer groups vs students, then stamp `currentRole`. */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragUser(null);
    if (!over) return;

    const userData = active.data.current?.user as UserWithProfile | undefined;
    if (!userData) return;

    const roleGroup = active.data.current?.roleGroup as string | undefined;

    if (isOrganizerRoleGroup(roleGroup)) {
      handleAddOrganizer({ ...userData, currentRole: roleGroupToOrganizerRole(roleGroup) });
    } else {
      handleAddStudent({ ...userData, currentRole: 'student' });
    }
  };

  /** Seed the users-search cache so the new organizer appears immediately. */
  const handleFacilitatorCreated = React.useCallback(
    (created: UserWithProfile) => {
      prependUserToUsersSearchCaches(queryClient, 'tutor', created);
      handleAddOrganizer(created);
    },
    [queryClient, handleAddOrganizer],
  );

  /** Newly created registered student — force `currentRole` to student. */
  const handleStudentCreated = React.useCallback(
    (created: UserWithProfile) => {
      handleAddStudent({ ...created, currentRole: 'student' });
    },
    [handleAddStudent],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex-1 min-h-0 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-3 min-h-0">
          <UsersListColumn
            role="all-organizers"
            emptyMessage="Aucun organisateur disponible"
            excludedIds={selectedOrganizerIds}
            pageSize={EVENT_PARTICIPANTS_COLUMN_PAGE_SIZE}
            onDoubleClick={handleAddOrganizer}
            onCreateClick={() => setIsFacilitatorCreationOpen(true)}
            initialEstablishmentId={establishmentId}
            initialEstablishmentName={establishmentName}
          />
        </div>

        <div className="col-span-12 lg:col-span-6 min-h-0">
          <div className="section-card h-full flex flex-col">
            <div className="section-header flex-shrink-0">
              <h3 className="section-title text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                Participants
              </h3>
              {(selectedOrganizers.length > 0 ||
                selectedStudents.length > 0 ||
                unregisteredParticipants.length > 0) && (
                <span className="text-sm font-medium text-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] px-2 py-1 rounded-full">
                  {selectedOrganizers.length +
                    selectedStudents.length +
                    unregisteredParticipants.length}
                </span>
              )}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pr-2 space-y-3">
              <UnifiedDropZone
                tutors={selectedOrganizers}
                students={selectedStudents}
                unregisteredStudents={unregisteredParticipants}
                onRemoveTutor={onRemoveOrganizer}
                onRemoveStudent={onRemoveStudent}
                onRemoveUnregistered={onRemoveUnregistered}
                onEditUnregistered={(idx) => setEditingUnregisteredIdx(idx)}
                mode="unified"
                tutorSectionLabel="Organisateurs"
                tutorGroupByRole
              />
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-3 min-h-0">
          <UsersListColumn
            role="student"
            emptyMessage="Aucun élève disponible"
            excludedIds={selectedStudentIds}
            pageSize={EVENT_PARTICIPANTS_COLUMN_PAGE_SIZE}
            onDoubleClick={handleAddStudent}
            onCreateClick={() => setIsStudentCreationOpen(true)}
            initialEstablishmentId={establishmentId}
            initialEstablishmentName={establishmentName}
          />
        </div>
      </div>

      <CustomDragOverlay activeUser={activeDragUser} />

      <EventFacilitatorPickerModal
        open={isFacilitatorCreationOpen}
        onOpenChange={setIsFacilitatorCreationOpen}
        onFacilitatorCreated={handleFacilitatorCreated}
        onRequestCreateTutor={onRequestCreateTutor}
      />

      <EventStudentPickerModal
        open={isStudentCreationOpen}
        onOpenChange={setIsStudentCreationOpen}
        onStudentCreated={handleStudentCreated}
        onUnregisteredAdded={onAddUnregistered}
      />

      <BaseModal
        open={editingUnregisteredIdx !== null}
        onClose={() => setEditingUnregisteredIdx(null)}
        size="md"
        header={{
          icon: Pencil,
          iconSize: 'md',
          title: 'Modifier le participant non inscrit',
          subtitle: 'Mettez à jour les informations du participant',
          titleSize: 'lg',
          titleWeight: 'semibold',
        }}
        footer={
          <>
            <Button preset="modal-cancel" onClick={() => setEditingUnregisteredIdx(null)}>
              Annuler
            </Button>
            <Button preset="modal-confirm" onClick={() => editFormRef.current?.submit()}>
              Enregistrer
            </Button>
          </>
        }
      >
        <UnregisteredStudentForm
          key={editingUnregisteredIdx}
          ref={editFormRef}
          initialData={
            editingUnregisteredIdx !== null
              ? unregisteredParticipants[editingUnregisteredIdx]
              : undefined
          }
          onAdd={(data) => {
            if (editingUnregisteredIdx !== null) {
              onEditUnregistered(editingUnregisteredIdx, data);
            }
            setEditingUnregisteredIdx(null);
          }}
        />
      </BaseModal>
    </DndContext>
  );
};
